/**
 * Normalize plan names to match credit pack naming convention.
 * 
 * Maps database plan names to credit pack IDs:
 * - 'starter' → 'basic'
 * - 'ultimate' → 'premium'
 * - 'pro' → 'pro' (unchanged)
 * 
 * This is needed because:
 * - Database uses: 'starter', 'pro', 'ultimate'
 * - Credit packs use: 'basic', 'pro', 'premium'
 */
export function normalizePlanName(planName: string | null | undefined): string {
  if (!planName) return '';
  
  const mapping: Record<string, string> = {
    'starter': 'basic',
    'basic': 'basic',
    'pro': 'pro',
    'ultimate': 'premium',
    'premium': 'premium'
  };
  
  return mapping[planName.toLowerCase()] || planName.toLowerCase();
}

/**
 * Filter credit packs to show only the one matching user's subscription tier.
 * 
 * @param allPacks - All available credit packs from API
 * @param userPlanId - User's plan_id from database ('starter' | 'pro' | 'ultimate' | null)
 * @returns Array with only the matching pack (or empty array if no match)
 */
export function getAvailablePacksForUser<T extends { id: string }>(
  allPacks: T[],
  userPlanId: string | null | undefined
): T[] {
  if (!userPlanId) {
    return []; // No subscription = no packs available
  }
  
  const normalizedPlan = normalizePlanName(userPlanId);
  
  if (!normalizedPlan) {
    return [];
  }
  
  // Return only the pack that matches the user's tier
  return allPacks.filter(pack => normalizePlanName(pack.id) === normalizedPlan);
}
